import React from 'react';
import type { Taxonomy, Candidate } from '@axiom/validation';
import {
  CandidateIndustryModel,
  SubmissionCandidateModel,
} from '@axiom/ui-models';

import { Divider } from '../../element/Divider/Divider';
import { Grid } from '../../layout/Grid/Grid';
import { GridColumn } from '../../layout/Grid/GridColumn';
import { GridRow } from '../../layout/Grid/GridRow';
import { Paragraph } from '../../content/Paragraph/Paragraph';
import { ParagraphHeader } from '../../content/ParagraphHeader/ParagraphHeader';
import { TaxonomyUtil } from '../../../utils/taxonomy-util';

export type CandidateHighlightExperienceDetailsProps = {
  isDownloading?: boolean;
  showPracticeArea?: boolean;
  candidate: Partial<
    Pick<
      Candidate,
      | 'industries'
      | 'primaryPracticeArea'
      | 'primaryFocusArea'
      | 'lawFirms'
      | 'addressState'
      | 'addressCountry'
    >
  >;
  taxonomy: Taxonomy;
};

export const CandidateHighlightExperienceDetails = ({
  isDownloading,
  showPracticeArea = false,
  candidate,
  taxonomy,
}: CandidateHighlightExperienceDetailsProps) => {
  // 1. Helpers
  const formatIndustryDisplay = (industry: {
    industryValue: string;
    yearsOfExperience: number;
  }) => {
    const yoe = Math.floor(industry.yearsOfExperience);
    const displayValue =
      taxonomy.industries[industry.industryValue]?.displayValue;
    const yearsLabel = yoe < 1 ? '(< 1 yr.)' : `(${yoe}+ yrs.)`;
    return `${displayValue} ${yearsLabel}`;
  };

  // 2. Derived data
  const industries = new CandidateIndustryModel(
    candidate.industries ?? []
  ).formatIndustries();
  const lawFirms = [...new Set(candidate.lawFirms?.map(item => item.client))];
  const address = new SubmissionCandidateModel({
    candidate,
  }).calculatedAddress();
  const practiceAreaDisplay =
    TaxonomyUtil.formatPracticeAreaFocusAreaForDisplay(
      taxonomy,
      candidate.primaryPracticeArea,
      candidate.primaryFocusArea
    );

  // 3. Existence checks
  const hasIndustries = industries.length > 0;
  const hasPracticeAreaDisplay = !!practiceAreaDisplay;
  const hasLawFirms = lawFirms.length > 0;
  const hasAddress = !!address;

  return (
    <Grid name="highlights-experience-details">
      {hasIndustries ? (
        <GridRow gutterBottom="16px" name="highlight-industries-section">
          <GridColumn
            largeScreenWidth={4}
            smallScreenWidth={isDownloading ? 12 : 4}
          >
            <ParagraphHeader>Industries</ParagraphHeader>
          </GridColumn>
          <GridColumn
            largeScreenWidth={8}
            smallScreenWidth={isDownloading ? 12 : 8}
          >
            <Paragraph name="highlight-industries">
              <Divider>
                {industries.map(industry => (
                  <span key={`industry-${industry.industryValue}`}>
                    {formatIndustryDisplay(industry)}
                  </span>
                ))}
              </Divider>
            </Paragraph>
          </GridColumn>
        </GridRow>
      ) : null}
      {showPracticeArea && hasPracticeAreaDisplay ? (
        <GridRow gutterBottom="16px" name="highlight-practicearea-section">
          <GridColumn
            largeScreenWidth={4}
            smallScreenWidth={isDownloading ? 12 : 4}
          >
            <ParagraphHeader>Practice Area</ParagraphHeader>
          </GridColumn>
          <GridColumn
            largeScreenWidth={8}
            smallScreenWidth={isDownloading ? 12 : 8}
          >
            <Paragraph name="highlight-practicearea">
              {practiceAreaDisplay}
            </Paragraph>
          </GridColumn>
        </GridRow>
      ) : null}
      {hasLawFirms ? (
        <GridRow gutterBottom="16px" name="highlight-lawfirms-section">
          <GridColumn
            largeScreenWidth={4}
            smallScreenWidth={isDownloading ? 12 : 4}
          >
            <ParagraphHeader>Law Firms</ParagraphHeader>
          </GridColumn>
          <GridColumn
            largeScreenWidth={8}
            smallScreenWidth={isDownloading ? 12 : 8}
          >
            <Paragraph name="highlight-lawfirms">
              <Divider>
                {lawFirms.map(client => (
                  <span key={`firm-${client}`}>{client}</span>
                ))}
              </Divider>
            </Paragraph>
          </GridColumn>
        </GridRow>
      ) : null}
      {hasAddress ? (
        <GridRow gutterBottom="16px" name="highlight-location-section">
          <GridColumn
            largeScreenWidth={4}
            smallScreenWidth={isDownloading ? 12 : 4}
          >
            <ParagraphHeader>Location</ParagraphHeader>
          </GridColumn>
          <GridColumn
            largeScreenWidth={8}
            smallScreenWidth={isDownloading ? 12 : 8}
          >
            <Paragraph name="highlight-location">{address}</Paragraph>
          </GridColumn>
        </GridRow>
      ) : null}
    </Grid>
  );
};
